import React, { useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AuditLabel from './AuditLabel';
import { formatDate } from '../utils/dateUtils';
import { intakeTokenFor } from '../utils/hotelUtils';
import { useLazyBookingSignatures, withSignatures } from '../hooks/useLazyBookingSignatures';

const HotelBookingCard = ({ booking, onEdit, onDelete, onCheckIn, onCheckOut, onEditVetCheckin }) => {
    const navigate = useNavigate();

    // Signatures are fetched only once the card is actually scrolled into view.
    const cardRef = useRef(null);
    const signatures = useLazyBookingSignatures([booking], cardRef)[booking._id];
    const checkInSignature  = signatures.checkIn;
    const checkOutSignature = signatures.checkOut;

    const bookingWithSignatures = useMemo(
        () => withSignatures(booking, signatures),
        [booking, checkInSignature, checkOutSignature]
    );

    const handleShareForm = () => {
        const url = `${window.location.origin}/hotel/intake/${intakeTokenFor(booking)}`;
        const ownerPhone = booking.dog?.owners?.[0]?.phone;
        const dogName = booking.dog?.name || 'tu perro';
        const text = `Hola! Te compartimos el formulario de ingreso de *${dogName}* al hotel. Por favor completalo antes de su llegada:\n${url}`;
        if (ownerPhone) {
            const clean = ownerPhone.replace(/\D/g, '');
            window.open(`https://wa.me/${clean}?text=${encodeURIComponent(text)}`, '_blank');
        } else if (navigator.share) {
            navigator.share({ title: 'Formulario de ingreso', url, text }).catch(() => {});
        } else {
            navigator.clipboard?.writeText(url);
            alert('Enlace copiado al portapapeles');
        }
    };
    const calculateDuration = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return 0;
        
        try {
            // Create dates safely handling timezone issues
            let startDate, endDate;
            
            if (typeof checkIn === 'string' && checkIn.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = checkIn.split('-');
                startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            } else {
                startDate = new Date(checkIn);
            }
            
            if (typeof checkOut === 'string' && checkOut.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = checkOut.split('-');
                endDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            } else {
                endDate = new Date(checkOut);
            }
            
            const timeDiff = endDate.getTime() - startDate.getTime();
            const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
            return Math.max(0, daysDiff);
        } catch (error) {
            return 0;
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'booked':
                return 'status-badge booked';
            case 'checked-in':
                return 'status-badge checked-in';
            case 'checked-out':
                return 'status-badge checked-out';
            case 'cancelled':
                return 'status-badge cancelled';
            default:
                return 'status-badge';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'booked':
                return 'Reservado';
            case 'checked-in':
                return 'Ingresado';
            case 'checked-out':
                return 'Salida';
            case 'cancelled':
                return 'Cancelado';
            default:
                return status;
        }
    };

    const canCheckIn = (status) => {
        return status === 'booked';
    };

    const canCheckOut = (status) => {
        return status === 'checked-in';
    };

    const canEdit = (status) => {
        return status === 'booked' || status === 'checked-in';
    };

    const canDelete = (status) => {
        return status === 'booked' || status === 'cancelled';
    };

    const ownerNames = booking.dog?.owners?.map(owner => owner.name).join(', ') || 'Propietario desconocido';
    const duration = calculateDuration(booking.checkIn, booking.checkOut);

    return (
        <div className="hotel-booking-card" ref={cardRef}>
            <div className="booking-header">
                <h3>🏨 {booking.dog?.name || 'Perro desconocido'}</h3>
                <div>
                    <span className={getStatusBadgeClass(booking.status)}>
                        {getStatusLabel(booking.status)}
                    </span>
                </div>
            </div>
            
            <div className="booking-info">
                <p className="dog-name">Perro: {booking.dog?.name || 'Desconocido'}</p>
                <p className="dog-breed">Raza: {booking.dog?.breed || 'Desconocida'}</p>
                <p className="owner-names">Propietario(s): {ownerNames}</p>
                <p className="checkin-date">Entrada: {formatDate(booking.checkIn)}</p>
                <p className="checkout-date">Salida: {formatDate(booking.checkOut)}</p>
                <p className="duration">Duración: {duration} noche{duration !== 1 ? 's' : ''}</p>
            </div>
            
            {booking.notes && (
                <div className="booking-notes">
                    <strong>Notas:</strong> {booking.notes}
                </div>
            )}

            {booking.vetCheckIn && (
                <div className="vet-checkin-info">
                    <h4>🩺 Evaluación de salud (entrada)</h4>
                    {booking.vetCheckIn.weight && (
                        <p><strong>Peso:</strong> {booking.vetCheckIn.weight} kg</p>
                    )}
                    {booking.vetCheckIn.temperature && (
                        <p><strong>Temperatura:</strong> {booking.vetCheckIn.temperature}°C</p>
                    )}
                    {booking.vetCheckIn.checkedBy && (
                        <p><strong>Revisado por:</strong> {booking.vetCheckIn.checkedBy}</p>
                    )}
                    {booking.vetCheckIn.notes && (
                        <p><strong>Notas de salud:</strong> {booking.vetCheckIn.notes}</p>
                    )}
                    {booking.vetCheckIn.medication && (
                        <p><strong>Medicación:</strong> {booking.vetCheckIn.medication}</p>
                    )}
                    {checkInSignature && (
                        <div className="signature-display">
                            <strong>Firma del propietario:</strong>
                            <img src={checkInSignature} alt="Firma del propietario" className="signature-display-img" />
                        </div>
                    )}
                    <p><strong>Fecha de evaluación:</strong> {formatDate(booking.checkIn)}</p>
                </div>
            )}

            {booking.vetCheckOut && (
                <div className="vet-checkout-info">
                    <h4>🩺 Evaluación de salud (salida)</h4>
                    {booking.vetCheckOut.weight && (
                        <p><strong>Peso:</strong> {booking.vetCheckOut.weight} kg</p>
                    )}
                    {booking.vetCheckOut.temperature && (
                        <p><strong>Temperatura:</strong> {booking.vetCheckOut.temperature}°C</p>
                    )}
                    {booking.vetCheckOut.checkedBy && (
                        <p><strong>Revisado por:</strong> {booking.vetCheckOut.checkedBy}</p>
                    )}
                    {booking.vetCheckOut.notes && (
                        <p><strong>Notas de salud:</strong> {booking.vetCheckOut.notes}</p>
                    )}
                    {booking.vetCheckOut.medication && (
                        <p><strong>Medicación:</strong> {booking.vetCheckOut.medication}</p>
                    )}
                    {checkOutSignature && (
                        <div className="signature-display">
                            <strong>Firma del propietario:</strong>
                            <img src={checkOutSignature} alt="Firma del propietario" className="signature-display-img" />
                        </div>
                    )}
                    <p><strong>Fecha de evaluación:</strong> {formatDate(booking.checkOut)}</p>
                </div>
            )}
            
            {/* Audit Trail */}
            <AuditLabel
                createdBy={booking.createdBy}
                updatedBy={booking.updatedBy}
                createdAt={booking.createdAt}
                updatedAt={booking.updatedAt}
                size="small"
            />
            
            <div className="card-actions">
                {/* Intake form */}
                <button
                    onClick={() => navigate(`/hotel/intake/${intakeTokenFor(booking)}/staff`)}
                    className="intake-form-btn"
                    title="Formulario de ingreso"
                >
                    {booking.intakeForm?.submittedAt ? '📋 Ver formulario' : '📋 Formulario de ingreso'}
                </button>
                {booking.intakeForm?.submittedAt && (
                    <button
                        onClick={() => navigate(`/hotel/intake/${intakeTokenFor(booking)}/summary`)}
                        className="intake-form-btn"
                        title="Ver resumen para el equipo"
                    >
                        🗒️ Ver resumen
                    </button>
                )}
                <button
                    onClick={handleShareForm}
                    className="share-form-btn"
                    title="Compartir formulario al dueño"
                >
                    📤 Compartir
                </button>

                {canEdit(booking.status) && onEdit && (
                    <button
                        onClick={() => onEdit(booking)}
                        className="edit-btn"
                        title="Editar reserva"
                    >
                        Editar
                    </button>
                )}

                {canCheckIn(booking.status) && onCheckIn && (
                    <button
                        onClick={() => onCheckIn(booking)}
                        className="checkin-btn"
                        title="Registrar entrada"
                    >
                        Entrada
                    </button>
                )}

                {booking.status === 'checked-in' && onEditVetCheckin && (
                    <button
                        onClick={() => onEditVetCheckin(bookingWithSignatures)}
                        className="vet-edit-btn"
                        title="Editar información de salud de entrada"
                    >
                        🩺 Salud entrada
                    </button>
                )}

                {canCheckOut(booking.status) && onCheckOut && (
                    <button
                        onClick={() => onCheckOut(booking)}
                        className="checkout-btn"
                        title="Registrar salida"
                    >
                        Salida
                    </button>
                )}

                {canDelete(booking.status) && onDelete && (
                    <button
                        onClick={() => onDelete(booking._id)}
                        className="delete-btn"
                        title="Eliminar reserva"
                    >
                        Eliminar
                    </button>
                )}
            </div>
        </div>
    );
};

export default HotelBookingCard;